import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import React, { Suspense, useState } from 'react';
import { Link } from 'react-router-dom';

import { sanitizeHtml } from '../../../utils/sanitizeHtml';
import { supabase } from '../../../utils/supabase';
import InfoModal from '../../modals/InfoModal';

const AboutQuillEditor = React.lazy(() => import('./AboutQuillEditor'));

function GolfCourseAbout({ course, user, editableCourses, editPermissions, username }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [localEditorContent, setLocalEditorContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const canEdit = editPermissions === 'Advanced';

  const createMarkup = () => {
    return { __html: sanitizeHtml(course?.about) };
  };

  const handleReadMoreClick = () => {
    setIsModalOpen(true);
  };

  const handleEditorChange = (content) => {
    setLocalEditorContent(content);
  };

  const handleCancel = () => {
    setEditMode(false);
    setLocalEditorContent('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('golf_courses')
        .update({ about: localEditorContent })
        .eq('course_name', course.course_name);

      if (error) throw error;
      
      // Update the course object locally to trigger re-render
      course.about = localEditorContent;
      
      setEditMode(false);
      setLocalEditorContent('');
      alert(course.about ? 'About section updated successfully' : 'About section added successfully');
    } catch (error) {
      console.error('Error updating about section:', error);
      alert('Failed to update about section');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="mx-auto w-full mt-1">
      <article
        id="golfCourseAbout"
        className="relative lg:h-[575px] max-h-[575px] w-full prose max-w-none prose-sm sm:prose-md text-left overflow-y-hidden p-6 rounded-xl bg-white shadow-lg flex flex-col"
      >
        <h2 className="text-center truncate flex-shrink-0">
          About: <span className="text-indigo-700">{course.course_name}</span>
        </h2>
        {course.course_name && course.course_style != null && course.course_style !== '' && course.course_county && (
          <p className="text-sm text-gray-700 flex-shrink-0">
            {course.course_name} is a {String(course.course_style).toLowerCase()},{' '}
            {course.number_holes} hole golf course in {course.course_county}.
            {course.opened_date &&
              ` The course first opened on this site in ${course.opened_date}.`}
          </p>
        )}

        {/* Edit Mode */}
        {editMode && (
          <div className="flex flex-col flex-1 min-h-0 mt-2">
            <p className="text-center text-sm italic px-2 mb-2 flex-shrink-0">
              Edit the about section for {course.course_name}.
            </p>
            <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 rounded">
              <Suspense
                fallback={
                  <div className="flex min-h-[200px] items-center justify-center bg-gray-50 text-gray-500 text-sm">
                    Loading editor…
                  </div>
                }
              >
                <AboutQuillEditor
                  value={localEditorContent}
                  onChange={handleEditorChange}
                />
              </Suspense>
            </div>
            <div className="flex justify-between items-center mt-2 px-2 pt-2 flex-shrink-0 border-t border-gray-200">
              <span className="text-sm text-gray-600">
                Word count: {localEditorContent ? localEditorContent.trim().split(/\s+/).length : 0}
              </span>
              <div className="space-x-2">
                <button
                  onClick={handleCancel}
                  className="bg-red-500 text-white py-2 px-4 rounded hover:bg-red-700"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-700"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Display Mode */}
        {!editMode && (
          <>
            {/* Edit Button for Advanced Users - Moved to top */}
            {canEdit && course.about !== undefined && (
              <button
                onClick={() => {
                  setEditMode(true);
                  setLocalEditorContent(course.about || '');
                }}
                className="mx-auto min-w-[200px] py-2 px-4 bg-blue-500 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center justify-center mb-4"
              >
                <FontAwesomeIcon icon={solid('user-pen')} className="mr-2" />
                {course.about ? 'Edit About Section' : 'Add About Section'}
              </button>
            )}

            {/* Container for the content with restricted height */}
            <div className="max-h-[200px] lg:max-h-[500px] overflow-hidden">
              {course.about === undefined ? (
                <div
                  className="space-y-2 animate-pulse py-1"
                  aria-busy="true"
                  aria-label="Loading about text"
                >
                  <div className="h-4 bg-gray-200 rounded w-full" />
                  <div className="h-4 bg-gray-200 rounded w-11/12" />
                  <div className="h-4 bg-gray-200 rounded w-4/5" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                </div>
              ) : (
                <div dangerouslySetInnerHTML={createMarkup()} />
              )}
            </div>

            {/* Minimalist Read More Button */}
            {course.about === null && (
              <p className="text-center italic text-gray-500">
                Apologies! Our research on {course.course_name} is still in
                progress. If you have any interesting facts about{' '}
                {course.course_name} please <Link to="/contact">contact us</Link>.
              </p>
            )}

            {/* Minimalist Read More Button */}
            {course.about && (
              <button
                className="absolute bottom-0 left-0 right-0 w-full rounded-xl bg-white border py-2 mx-auto text-center text-indigo-600 hover:text-indigo-800 transition ease-in-out duration-300 text-sm underline"
                onClick={handleReadMoreClick}
              >
                Read More...
              </button>
            )}
          </>
        )}
      </article>

      {/* Modal for showing all content */}
      <InfoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`About: ${course.course_name}`}
      >
        <article
          className="prose prose-sm sm:prose-lg text-left"
          dangerouslySetInnerHTML={createMarkup()}
        />
      </InfoModal>
    </section>
  );
}

export default GolfCourseAbout;
